import type { CopyKey } from 'locales/copy-keys.gen'
import type { ChartPoint, ReturnStats } from 'models/chart'

/**
 * A product's chart as the STO page shows it, one kind per product: each kind maps its own
 * price source in its own file (stock.ts, fund.ts, miningNote.ts). A new chart kind is a new
 * file and a new entry in `ChartKind`.
 */
export type ChartKind = 'stock' | 'fund' | 'miningNote'

/** A mining note's extra series, per note in its currency. */
export interface NoteSeries {
  /** While it runs (BMN2): the issuance amount over time, its first tab and its hero chart. */
  issuance?: ChartPoint[]
  /** Once matured (BMN1): the nominal held in BTC from the first day, to compare with. */
  buyAndHold?: { points: ChartPoint[]; nominal: number }
  /**
   * A note without mining data (PKH2), charted from its supply: the notes outstanding per day, behind
   * its issuance amount (× the token price) and the figures under its chart.
   */
  supply?: ChartPoint[]
  /** BTC mined per note so far: the newest day's cumulative rewards. */
  minedBtc?: number
  /** The issuance amount's currency, when the BTC mined is charted in BTC (BMN2). */
  issuanceCurrency?: string
}

/** One currency the chart can be shown in. */
export interface ChartDenomination {
  currency: string
  points: ChartPoint[]
  /**
   * What returns are measured on, when not the plotted values: Goldstream's published monthly alpha
   * compounded (100 at the first point), since its 6-decimal XAUt NAV is too coarse for its returns.
   */
  returnIndex?: ChartPoint[]
}

/** A product's headline chart: what the hero card and the full chart show. */
export interface ProductChart {
  /** `stock`: equity underlying; `fund`: unit NAV; `miningNote`: BTC mined per note (BMN1, BMN2). */
  kind: ChartKind
  /** Short symbol shown next to the value: the underlying ticker, or the product's token. */
  symbol: string
  /** What the values are, next to the symbol ("Underlying stock", "Fund NAV", "BTC mined"). */
  labelKey: CopyKey
  /** Default denomination first. More than one lets the chart switch (Goldstream: XAUt / USD). */
  denominations: ChartDenomination[]
  /** Mining notes only (BMN1, BMN2): what they chart besides the BTC mined. */
  note?: NoteSeries
  /** Funds whose units are known: the total NAV (unit NAV × units outstanding) per NAV date, per currency. */
  fundNavs?: ChartDenomination[]
  /** Date of the latest point, pre-formatted (UTC) so server and client render the same text. */
  asOf?: string
  /** Display name of the data vendor, when the data says. */
  source?: string
  /** Logo before the symbol, only when there is one (equity: the underlying stock's). */
  icon?: string
}

/** One column of the history table and CSV file: a denomination's full series. */
export interface HistoryColumn {
  currency: string
  /** Column heading, with `{currency}`. */
  headerKey: CopyKey
  points: ChartPoint[]
}

/** What the hero card and the Performance section render. */
export interface ProductChartView {
  chart: ProductChart
  /** Fund only: since launch, per currency — independent of the range picked on the chart. */
  statsByCurrency: Record<string, ReturnStats>
  /** Full history per denomination, for the table and the CSV download. */
  history: HistoryColumn[]
}

/**
 * Values computed from the product's chart, offered next to the project fields in the back-office
 * metric dropdown as `{{computed.…}}` (back-office `COMPUTED_FIELDS`). Performance: funds from their
 * unit NAV, equity notes from the underlying stock's price; mining notes have no price to measure.
 * Fund NAV: the fund's total NAV now, its latest unit NAV × its units outstanding.
 * Underlying price: an equity note's underlying stock, its latest close, as the hero chart shows it.
 * BTC mined: a mining note's BTC mined per note so far, from its mining data, new each day.
 */
export const COMPUTED_FIELDS = {
  performanceYtd: 'computed.performanceYtd',
  performanceSinceInception: 'computed.performanceSinceInception',
  fundNav: 'computed.fundNav',
  underlyingPrice: 'computed.underlyingPrice',
  btcMined: 'computed.btcMined',
} as const

export type ComputedFieldPath = (typeof COMPUTED_FIELDS)[keyof typeof COMPUTED_FIELDS]

/** A change in % — or, with `currency`, an amount in it. */
export interface ComputedValue {
  value: number
  currency?: string
  /** An amount's decimals; whole by default. */
  decimals?: number
}

/** A field the chart cannot give is absent. */
export type ComputedValues = Partial<Record<ComputedFieldPath, ComputedValue>>

/**
 * `pending`: the chart is still loading (it is client-only), so a computed metric holds its slot.
 * `values`: once loaded; a field missing from it is left out like an empty project field.
 */
export interface ComputedFields {
  pending: boolean
  values: ComputedValues
}
